import { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

// Layouts
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import DefaultTiersLoader from "@/Layouts/components/Skeletons/DefaultTiersLoader/DefaultTiersLoader";
import TabLoader from "@/Layouts/components/Skeletons/TabLoader";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";

import VariableOpsGpsBonusMatrix from "../CommissionMatrix";

// MUI
import { BuildVariableOperationsGpsBonusSetup } from "@/PayPlans/helpers/DefaultSections";
import { IVariableOperationsGpsBonusSetup, IVariableOperationsGpsBonusTiers } from "@/PayPlans/types/sections/variableOperationsGpsBonus";
import { Grid } from "@mui/material";
import { cloneDeep, isEqual } from "lodash";
import { DEFAULT_MINIMUM_COLUMNS, DEFAULT_MINIMUM_TIERS } from "../CommissionMatrix/constants";
import { IBonusTier } from "../CommissionMatrix/types";
import { SwitchKey, TabKey, TabModel, VariableOperationsGpsBonusModel } from "./types";

interface VariableOperationsGpsBonusProps {
	sectionId?: string;
	position?: string;
	content: IVariableOperationsGpsBonusSetup;
	franchises?: string[];
	loading?: boolean;
	saving?: boolean;
	readOnly?: boolean;
	useSectionsErrors?: () => any;
	onActionUpdate: (section: string, area: null, data: IVariableOperationsGpsBonusSetup, isValid: boolean) => void;
}

const getFranchiseTabKey = (make: string) => `${TabKey.NEW_VEHICLES}_${make}`;
const minimumColumns = DEFAULT_MINIMUM_COLUMNS;
const minimumTiers = DEFAULT_MINIMUM_TIERS;

const VariableOperationsGpsBonus: FC<VariableOperationsGpsBonusProps> = (props) => {
	const {
		sectionId = "variableOperationsGpsBonus",
		position,
		content,
		loading = false,
		saving = false,
		readOnly = false,
		useSectionsErrors,
		onActionUpdate,
	} = props;

	const { errors, hasSomeError, clearSectionErrors, addSectionError, removeSectionError, getErrorKey } = useSectionsErrors?.();

	const [innerModel, setInnerModel] = useState<VariableOperationsGpsBonusModel | null>(null);
	const [tabIndex, setTabIndex] = useState<number>(0);
	const propagateChangesRef = useRef(false);

	const defaultSetup = useMemo(() => {
		const { combined } = BuildVariableOperationsGpsBonusSetup();
		return combined.tiers[0];
	}, []);

	const hasDefaultSetup = useCallback((tiers: IBonusTier[]) => tiers.length === 1 && isEqual(tiers[0], defaultSetup), [defaultSetup]);

	const selectedTab = useMemo(() => {
		if (!innerModel?.tabs) return null;

		const selectedTab = innerModel.tabs[tabIndex];
		selectedTab.valid = !errors?.get(sectionId)?.includes(getErrorKey(sectionId, selectedTab.key));
		return selectedTab;
	}, [innerModel?.tabs, tabIndex, errors]);

	const tabsErrors = useMemo(() => {
		if (!innerModel?.tabs.length) return [];
		const componentErrors = errors.get(sectionId) || [];
		return innerModel.tabs.map((tab: TabModel) => componentErrors.includes(getErrorKey(sectionId, tab.key)));
	}, [errors, innerModel?.tabs, sectionId]);

	const disableTabValidation = useCallback(
		(tab: TabModel) => {
			const isSetupSaved = Boolean(tab.setupId);
			if (saving) return true;
			if (!hasDefaultSetup(tab.tiers)) return false;
			if (isSetupSaved) return true;

			if (selectedTab?.key !== tab.key) {
				return !!selectedTab?.valid;
			}

			return false;
		},
		[innerModel?.tabs, tabsErrors, selectedTab, saving]
	);

	const buildTabs = (content: IVariableOperationsGpsBonusSetup) => {
		const { _id, combined, newVehicles, preOwned, combineFranchises } = content;

		const preOwnedTab = {
			setupId: preOwned._id,
			key: TabKey.PRE_OWNED,
			label: preOwned.make,
			tiers: preOwned.tiers,
			valid: !!preOwned._id && !hasDefaultSetup(preOwned.tiers),
			evaluationType: preOwned.evaluationType,
		} as TabModel;

		const tabs = combineFranchises
			? [
					{
						setupId: combined._id,
						key: TabKey.COMBINED,
						label: combined.make,
						tiers: combined.tiers,
						valid: !!combined._id && !hasDefaultSetup(combined.tiers),
						evaluationType: combined.evaluationType,
					} as TabModel,
					preOwnedTab,
				]
			: [
					...newVehicles.map(
						(franchise: IVariableOperationsGpsBonusTiers) =>
							({
								setupId: _id && `${_id}_${franchise.make}`,
								key: getFranchiseTabKey(franchise.make),
								label: franchise.make,
								tiers: franchise.tiers,
								valid: !!(franchise._id || _id) && !hasDefaultSetup(franchise.tiers),
								evaluationType: franchise.evaluationType,
							}) as TabModel
					),
					preOwnedTab,
				];

		return tabs;
	};

	const handleValidation = (tabKey: string, valid: boolean) => {
		if (valid) {
			removeSectionError?.(sectionId, tabKey);
		} else {
			addSectionError?.(sectionId, tabKey);
		}
	};

	const handleUpdateSwitches = (switchKey: SwitchKey, value: boolean) => {
		if (!innerModel) return;
		const updatedModel = { ...innerModel };

		if (switchKey === "combineFranchises") {
			updatedModel.combineFranchises = value;
			updatedModel.tabs = buildTabs({ ...content, combineFranchises: value });
			setTabIndex(0);
			propagateChangesRef.current = false;
			clearSectionErrors?.(sectionId);
		}

		if (switchKey === "globalUnitCounts") {
			updatedModel.globalUnitCounts = value;
			propagateChangesRef.current = !!selectedTab?.valid;
		}

		setInnerModel(updatedModel);
	};

	const handleUpdateTiers = (data: IBonusTier[]) => {
		if (!innerModel) return;
		const updatedModel = { ...innerModel };
		updatedModel.tabs[tabIndex].tiers = data;

		propagateChangesRef.current = true;
		setInnerModel(updatedModel);
	};

	const handleEvaluationTypeUpdate = (evaluationType: string) => {
		if (!innerModel) return;
		const updatedModel = { ...innerModel };
		const currentTab = updatedModel.tabs[tabIndex];
		currentTab.evaluationType = evaluationType;

		propagateChangesRef.current = true;
		setInnerModel(updatedModel);
	};

	const submit = () => {
		if (!innerModel) return;
		const updatedNewVehicles: IVariableOperationsGpsBonusTiers[] = content.newVehicles.map(
			(franchise: IVariableOperationsGpsBonusTiers) => {
				const franchiseTab = innerModel.tabs.find((tab: TabModel) => tab.key === getFranchiseTabKey(franchise.make));
				return {
					...franchise,
					tiers: franchiseTab?.tiers || franchise.tiers,
					evaluationType: franchiseTab?.evaluationType || franchise.evaluationType,
				};
			}
		);

		const preOwnedTab = innerModel.tabs.find((tab: TabModel) => tab.key === TabKey.PRE_OWNED);
		const combinedTab = innerModel.tabs.find((tab: TabModel) => tab.key === TabKey.COMBINED);

		const sanitizedSetup: IVariableOperationsGpsBonusSetup = {
			...content,
			newVehicles: updatedNewVehicles,
			preOwned: {
				...content.preOwned,
				tiers: preOwnedTab?.tiers || content.preOwned.tiers,
				evaluationType: preOwnedTab?.evaluationType || content.preOwned.evaluationType,
			},
			combined: {
				...content.combined,
				tiers: combinedTab?.tiers || content.combined.tiers,
				evaluationType: combinedTab?.evaluationType || content.combined.evaluationType,
			},
			combineFranchises: Boolean(innerModel.combineFranchises),
			globalUnitCounts: Boolean(innerModel.globalUnitCounts),
		};

		propagateChangesRef.current = false;
		onActionUpdate(sectionId, null, cloneDeep(sanitizedSetup), true);
	};

	useEffect(() => {
		if (!hasSomeError && propagateChangesRef.current) {
			submit();
		}
	}, [errors, hasSomeError, selectedTab, innerModel]);

	useEffect(() => {
		propagateChangesRef.current = false;
		setInnerModel({
			tabs: buildTabs(content),
			globalUnitCounts: Boolean(content.globalUnitCounts),
			combineFranchises: Boolean(content.combineFranchises),
		});

		return () => {
			clearSectionErrors?.(sectionId);
		};
	}, [content]);

	return (
		<Grid container>
			<Grid item xs={12} mt={2.5}>
				{loading || !innerModel ? (
					<Grid>
						<TabLoader tabs={2} switch bottomSwitch />
						<DefaultTiersLoader mt={1} buttons />
					</Grid>
				) : (
					<Fragment>
						<TabSelectable
							tabs={innerModel.tabs.map((tab: TabModel) => tab.label) || []}
							tabSelected={tabIndex}
							onTabChange={setTabIndex}
							errors={tabsErrors}
							content={innerModel.tabs.map((tab: TabModel, index: number) => (
								<Grid container key={`${tab.key}_${index}`}>
									<Grid item xs={12} className={"models-tiered-area"}>
										<VariableOpsGpsBonusMatrix
											testId={`variable-ops-gps-bonuses-${tab.key}`}
											tiers={tab.tiers}
											position={position}
											readOnly={readOnly || saving}
											rowTitle={"Deals out of Compliance"}
											rowTitleTooltip={"Descending Amounts"}
											columnTitle={"Unregistered Consumers"}
											columnTitleTooltip={"Descending Amounts"}
											columnLabel={"Bonus"}
											minimumColumns={minimumColumns}
											minimumTiers={minimumTiers}
											evaluationType={tab.evaluationType}
											disableValidation={disableTabValidation(tab)}
											onActionUpdate={handleUpdateTiers}
											onActionValidation={(valid) => handleValidation(tab.key, valid)}
											onEvaluationTypeUpdate={handleEvaluationTypeUpdate}
										/>
									</Grid>
								</Grid>
							))}
							topActions={[
								<CustomizedSwitches
									key={SwitchKey.GLOBAL_UNIT_COUNTS}
									textChecked="Global Unit Counts"
									textUnChecked="Global Unit Counts"
									checked={innerModel?.globalUnitCounts}
									disabled={readOnly}
									onChange={(value) => handleUpdateSwitches(SwitchKey.GLOBAL_UNIT_COUNTS, value)}
									tooltip={{
										title: "Uses all units of New and Pre-Owned Vehicles together.",
										placement: "bottom",
										key: "model-commissions-global-unit-counts-tooltip",
									}}
								/>,
								<CustomizedSwitches
									key={SwitchKey.COMBINE_FRANCHISES}
									textChecked="Combine Franchises"
									textUnChecked="Combine Franchises"
									checked={innerModel?.combineFranchises}
									disabled={readOnly}
									onChange={(value) => handleUpdateSwitches(SwitchKey.COMBINE_FRANCHISES, value)}
									tooltip={{
										title: "Combines franchises into a single definition.",
										placement: "bottom",
										key: "market-share-simultaneous-ranking-tooltip",
									}}
								/>,
							]}
						/>
					</Fragment>
				)}
			</Grid>
		</Grid>
	);
};

export default VariableOperationsGpsBonus;
